import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { matrix3d } from "./homography";
import { layerUrl, SCENES } from "./scenes";
import type { SceneName } from "./scenes";

// One mockup photo with the print faces bent into it. Layers, bottom to top: the photo with its green
// blank painted out, the faces (matrix3d onto each target, softened to the photo's sharpness and given
// a matte-ink look), the room's light (multiply), the reflections (screen), and anything that sits on
// top of the print (a thumb). Faces render only once the scene comes near the screen.

// ink on matte paper: calmer colour, lifted blacks
const INK = "saturate(.92) contrast(.94) brightness(1.025)";

export interface MockupSceneProps {
  scene: SceneName;
  /** one face per target, repeated when there are fewer */
  faces: React.ReactNode[];
  /** the faces' drawn size in px */
  faceSize: [number, number];
  label: string;
}

export const MockupScene: React.FC<MockupSceneProps> = ({ scene, faces, faceSize, label }) => {
  const s = SCENES[scene];
  const ref = useRef<HTMLDivElement>(null);
  const [px, setPx] = useState(0);
  const [near, setNear] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setPx(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => setPx(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && setNear(true), { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [near]);

  const [fw, fh] = faceSize;
  const k = px / s.width;
  return (
    <div className="ms-scene" ref={ref} role="img" aria-label={label} style={{ aspectRatio: `${s.width} / ${s.height}` }}>
      <img className="ms-layer" src={layerUrl(scene, "base")} alt="" loading="lazy" decoding="async" />
      {near && px > 0 && (
        <div className="ms-faces" aria-hidden="true">
          {s.targets.map((q, i) => {
            const side = Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]);
            const blur = (s.soft * fw) / side;
            return (
              <div
                key={i}
                className="ms-face"
                style={{
                  width: fw,
                  height: fh,
                  transform: matrix3d(fw, fh, q.map(([x, y]) => [x * k, y * k] as [number, number])),
                  filter: `${INK} blur(${blur.toFixed(2)}px)`,
                }}
              >
                {faces[i % faces.length]}
              </div>
            );
          })}
        </div>
      )}
      <img className="ms-layer ms-light" src={layerUrl(scene, "light")} alt="" loading="lazy" decoding="async" />
      <img className="ms-layer ms-sheen" src={layerUrl(scene, "sheen")} alt="" loading="lazy" decoding="async" />
      {s.over && <img className="ms-layer" src={layerUrl(scene, "over")} alt="" loading="lazy" decoding="async" />}
    </div>
  );
};

export default MockupScene;
